import { useLazyQuery, useQuery } from "@apollo/client/react";
import { Box, CircularProgress, Grid } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CustomTabs, { TabPanels } from '../../../components/tabs/Tabs';
import GET_CONTRATOS_ASISTENCIAL_BY_PACIENTE from '../../../data/graphQL/queries/GestionAsistencial/getContratosAsistencial';
import GET_GESTION_ASISTENCIAL_PACIENTE_BY_ID, { adaptPacienteGestionAsistencial } from '../../../data/graphQL/queries/GestionAsistencial/getGestionAsistencialPacienteById';
import GET_GESTION_ASISTENCIAL_UBICACION, { adaptUbicacion } from '../../../data/graphQL/queries/GestionAsistencial/getGestionAsistencialUbicacion';
import { GET_PROFESIONAL_LOGGED } from '../../../data/graphQL/queries/MisVisitas/getProfesionalLogged';
import { adaptTipoDocumentoArray, GET_ALL_TIPO_DOCUMENTO } from '../../../data/graphQL/queries/PacienteProbable/getAllTipoDocumento';
import ContratosSearcher from './components/ContratosSearcher';
import { HeaderChip } from './components/HeaderChip';
import PatientCard from './components/PatientCard';
import './gestionAsistencialCreationContent.scss';
import { tabs } from './tabs/tabs';

export const GestionAsistencialCreationContent = () => {

  const location = useLocation();
  const {
    idEvaluacion,
    idPaciente,
    allowedWrite,
    estadosPacienteOptions,
    delegacionesUsuario,
    idDelegacion,
    activeTab = 0
  } = location.state;

  const pageLoading = false;
  // por default en los tabs oculto BNP, al cargar la evaluación se verifica si debe mostrarse
  const [tabsGestionAsist, setTabsGestionAsist] = useState(tabs.filter((x) => x.id != 10));

  const [allowedWriteDetail, setAllowedWriteDetail] = useState(allowedWrite);


  const initialTabIndex = 0;
  const [currentTabIndex, setCurrentTabIndex] = useState(activeTab);
  const [currentTab, setCurrentTab] = useState({});
  const [idEvaluacionSelected, setIdEvaluacionSelected] = useState(idEvaluacion);

  const [evaluacionSelected, setEvaluacionSelected] = useState({});
  const [pacienteData, setPacienteData] = useState({});
  const [ubicacionData, setUbicacionData] = useState({});
  const [contratosData, setContratosData] = useState([]);
  const [contratoInicialData, setContratoInicialData] = useState({});
  const [profesionalLogged, setProfesionalLogged] = useState({});
  const [poseeEsquema, setPoseeEsquema] = useState(false);
  const [cambioDireccion, setCambioDireccion] = useState(false);
  const [documentosOptions, setDocumentosOptions] = useState([]);

  const { data: dataProfesionalLogged } = useQuery(GET_PROFESIONAL_LOGGED, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataProfesionalLogged) return;
    if (!!dataProfesionalLogged) {
      setProfesionalLogged(dataProfesionalLogged?.profesionalByIdUsuario);
    }
  }, [dataProfesionalLogged]);

  const { data: dataTipoDocumento } = useQuery(GET_ALL_TIPO_DOCUMENTO);

  useEffect(() => {
    if (!dataTipoDocumento) return;
    setDocumentosOptions(adaptTipoDocumentoArray(dataTipoDocumento));
  }, [dataTipoDocumento]);

  const [getUbicacionAsistencia, { data: dataUbicacionAsistencial }] = useLazyQuery(GET_GESTION_ASISTENCIAL_UBICACION, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (!dataUbicacionAsistencial) return;
    setUbicacionData({
      ...adaptUbicacion(dataUbicacionAsistencial?.pacienteSAP)
    });
  }, [dataUbicacionAsistencial]);

  useEffect(() => {
    if (!pacienteData.codigoSap || !idEvaluacionSelected) return;

    getUbicacionAsistencia({
      variables: {
        codPaciente: pacienteData.codigoSap,
        idEvaluacion: idEvaluacionSelected
      }
    });
  }, [pacienteData?.codigoSap, idEvaluacionSelected]);

  const labelPlan = useMemo(() => `#(${evaluacionSelected?.codigoSap}) ${evaluacionSelected?.modulo} - ${evaluacionSelected?.nombrePlan}`, [evaluacionSelected]);

  const ubicacionCompleta = useMemo(() => (
    {
      ...ubicacionData,
      latitud: pacienteData.latitud,
      longitud: pacienteData.longitud,
      telefonoUbicacion: pacienteData.telefonoUbicacion,
      observacionesUbicacion: pacienteData.observacionesUbicacion
    }),
    [ubicacionData, pacienteData]);

  const [getPaciente, { data: dataPaciente }] = useLazyQuery(GET_GESTION_ASISTENCIAL_PACIENTE_BY_ID, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (!dataPaciente) return;
    setPacienteData((prev) => ({ ...prev, ...adaptPacienteGestionAsistencial(dataPaciente.pacienteById) }));
  }, [dataPaciente]);


  const [getContratos, { data: dataContratosAsistencial, loading: responseLoading }] = useLazyQuery(GET_CONTRATOS_ASISTENCIAL_BY_PACIENTE, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (!dataContratosAsistencial) return;
    setContratosData(dataContratosAsistencial.contratosAsistencial);
    if (dataContratosAsistencial.contratosAsistencial.length > 0) {
      setContratoInicialData(dataContratosAsistencial.contratosAsistencial[dataContratosAsistencial.contratosAsistencial.length - 1]);
    } else {
      setContratoInicialData({});
    }
  }, [dataContratosAsistencial]);


  useEffect(() => {
    if (!idPaciente) return;
    getContratos({ variables: { idPaciente } });
  }, [idPaciente]);

  useEffect(() => {
    if (!evaluacionSelected || !evaluacionSelected?.codigoSAPCliente || !evaluacionSelected?.razonSocial) return;
    setPacienteData((prev) => ({
      ...prev,
      numeroAfiliado: evaluacionSelected.numeroAfiliado,
      financiador: `${evaluacionSelected?.codigoSAPCliente} - ${evaluacionSelected?.razonSocial}`
    }));

    // Tipo módulo AP visualiza el tab BNP 
    if ([2, 5, 6, 10, 14, 17].includes(evaluacionSelected.idTipoModulo)) {
      setTabsGestionAsist(tabs);
    } else {
      setTabsGestionAsist(tabs.filter((x) => x.id != 10));
    }

    // si la delagacion del contrato NO está dentro de las delegaciones que puede ver el usuario, NO permito editar
    setAllowedWriteDetail(allowedWrite && delegacionesUsuario.some(x => x.id == evaluacionSelected?.idDelegacion))
  }, [evaluacionSelected, idEvaluacionSelected]);

  useEffect(() => {
    if (!idPaciente) return;
    getPaciente({ variables: { idPaciente } });
  }, [idPaciente]);

  useEffect(() => {
    setIdEvaluacionSelected(idEvaluacion);
  }, [idEvaluacion]);


  const handleChangeTab = (tabIndex) => {
    setCurrentTabIndex(tabIndex);
    setCurrentTab(tabsGestionAsist[tabIndex]);
  };

  if (pageLoading) return <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '20vh'
    }}>
    <CircularProgress size={40} />
  </Box>;

  return (
    <div className='gestion-asistencial-creation-content'>
      <Grid container>
        <Grid container spacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <ContratosSearcher
              idPaciente={idPaciente}
              disabled={false}
              idEvaluacion={idEvaluacionSelected}
              onSelectIdEvaluacion={setIdEvaluacionSelected}
              onSelectEvaluacion={setEvaluacionSelected}
              contratos={contratosData}
              responseLoading={responseLoading}
              setPoseeEsquema={setPoseeEsquema}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <HeaderChip
              delegacion={evaluacionSelected?.delegacion}
              labelPlan={labelPlan}
            />
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <PatientCard
            evaluacionSelected={evaluacionSelected}
            estadosPacienteOptions={estadosPacienteOptions}
            labelPlan={labelPlan}
            contratoInicial={contratoInicialData}
          />
        </Grid>
        <Grid sx={{ width: '100%' }} size={{ xs: 12 }}>
          <CustomTabs
            tabs={tabsGestionAsist}
            currentTabIndex={currentTabIndex}
            initialTabIndex={initialTabIndex}
            handleChangeTab={handleChangeTab}
          />
          <div className='tab-content-container'>
            <div className='tab-content'>
              <TabPanels
                tabs={tabsGestionAsist}
                initialTabIndex={initialTabIndex}
                currentTabIndex={currentTabIndex}
                evaluacionSelected={evaluacionSelected}
                estadosPacienteOptions={estadosPacienteOptions}
                idEvaluacionSelected={idEvaluacionSelected}
                documentosOptions={documentosOptions}
                pacienteData={pacienteData}
                ubicacionData={ubicacionCompleta}
                allowedWrite={allowedWriteDetail}
                idPaciente={idPaciente}
                idEvaluacion={idEvaluacion}
                idDelegacion={idDelegacion}
                getContratos={getContratos}
                profesionalLogged={profesionalLogged}
                planCuidadoPoseeEsquema={poseeEsquema}
                contratoInicialData={contratoInicialData}
                cambioDireccion={cambioDireccion}
                setCambioDireccion={setCambioDireccion}
              />
            </div>
          </div>
        </Grid>
      </Grid>
    </div>);

};